import React from 'react';
import { fmtCount, fmtDate, fmtRelativeTime } from '../lib/format';
import StatTile from '../StatTile';

/** A small glyph for a Google surface tag (web / image / video / news). */
const SurfaceGlyph = (children) => (
  <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const SURFACE_ICONS = {
  web: SurfaceGlyph(<><circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3c2.5 2.6 2.5 15.4 0 18M12 3c-2.5 2.6-2.5 15.4 0 18" /></>),
  image: SurfaceGlyph(<><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></>),
  video: SurfaceGlyph(<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M10 9l5 3-5 3z" /></>),
  news: SurfaceGlyph(<><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 8h7M7 12h10M7 16h6" /></>),
};
/** The tag comes from Google's enum; anything unrecognised falls back to the web glyph. */
const surfaceIcon = (t) => SURFACE_ICONS[String(t).toLowerCase()] || SURFACE_ICONS.web;

/**
 * Evidence for `data_coverage` — the one panel that is trustworthy on day one.
 *
 * `facts`: { earliest_date, latest_date, days_stored, year_over_year_available,
 *            search_types, last_extraction, client_id, search_types_with_no_data }
 *
 * Never displayed (F15):
 *  - `client_id` — an internal identifier.
 *  - `search_types_with_no_data` — it exists so support can tell "no traffic on that
 *    surface" from "we never pulled that surface". To an owner the two read the same,
 *    and naming empty surfaces invites "why is Discover zero?" questions with no answer.
 *  - `last_extraction.site_url` — the raw property identifier (`sc-domain:…`). Internal.
 */
const CoveragePanel = ({ facts }) => {
  const searchTypes = Array.isArray(facts?.search_types) ? facts.search_types : [];
  const extraction = facts?.last_extraction || null;

  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        {/* A span of time, then the two dates that bound it — clock, then calendars. */}
        {fmtCount(facts?.days_stored) && (
          <StatTile
            label="Days of history"
            value={fmtCount(facts.days_stored)}
            icon="clock"
            tone="teal"
            description="How far back your saved search data goes."
            big
          />
        )}
        {fmtDate(facts?.earliest_date) && (
          <StatTile
            label="Earliest day on record"
            value={fmtDate(facts.earliest_date)}
            icon="calendar"
            tone="stone"
            description="The first day we have data for."
          />
        )}
        {fmtDate(facts?.latest_date) && (
          <StatTile
            label="Most recent day"
            value={fmtDate(facts.latest_date)}
            icon="flag"
            tone="rose"
            description="The latest day included."
          />
        )}
      </dl>

      {searchTypes.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Where you are showing up
          </p>
          <ul className="mt-1 flex flex-wrap gap-2">
            {searchTypes.map((t) => (
              <li
                key={t}
                className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 px-2.5 py-1 text-sm text-gray-700 dark:text-gray-300"
              >
                <span className="text-gray-400 dark:text-gray-500">{surfaceIcon(t)}</span>
                {t}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* One paragraph: the year-over-year caveat (when it applies) leads straight into
          why this record outgrows Google's own — Search Console keeps 16 months and
          discards the rest, while every day we pull is kept, so the gap widens daily. */}
      <p className="text-sm text-gray-500 dark:text-gray-400">
        {facts?.year_over_year_available === false
          && 'Not enough history yet to compare against the same period last year. '}
        Google keeps 16 months of search history. We keep every day since we started, so
        this record grows longer than Google's own with each passing day.
      </p>

      {extraction?.extracted_at && (
        <p className="text-xs text-gray-400 dark:text-gray-500">
          Last pulled {fmtRelativeTime(extraction.extracted_at)}
          {fmtCount(extraction.total_rows) ? ` · ${fmtCount(extraction.total_rows)} rows` : ''}
        </p>
      )}
    </div>
  );
};

export default CoveragePanel;
